import { Button, Flex, Text, TextInput } from '@mantine/core';
import { DatePickerInput, DateValue } from '@mantine/dates';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { observer } from 'mobx-react';
import { useForm } from '@mantine/form';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { z } from 'zod';
import { DateTime } from 'luxon';
import { BaseModal, MyIcon } from '@web/common-ui';
import {
  StammdatenQuestionnaireQuestionKey,
  stammdatenQuestionnaireReqDto,
} from '@web/common';
import {
  useOnboardingChecklistStore,
  useParticipantDataStore,
} from '../../../../store';

const { Address, PLZ, Ort } = StammdatenQuestionnaireQuestionKey;

// reuses the real DTO field definitions (lengths, PLZ range); the address is optional
// in the DTO, but in this form all three fields are required and must not be blank.
// movedAt here is a Date for the picker; it is converted to an ISO date string
// before being sent, to match updateParticipantAddressReqDto.
const updateAddressFormSchema = z.object({
  [Address]: stammdatenQuestionnaireReqDto.shape[Address].unwrap()
    .trim()
    .min(1),
  [PLZ]: stammdatenQuestionnaireReqDto.shape[PLZ],
  [Ort]: stammdatenQuestionnaireReqDto.shape[Ort].trim().min(1),
  movedAt: z.date(),
});

type UpdateAddressFormValues = {
  [Address]: string;
  [PLZ]: string;
  [Ort]: string;
  movedAt: Date | null;
};

type Props = {
  opened: boolean;
  onClose: () => void;
};

export const UpdateAddressModal = observer(({ opened, onClose }: Props) => {
  const participantDataStore = useParticipantDataStore();
  const onboardingChecklistStore = useOnboardingChecklistStore();
  const { t, i18n } = useTranslation('update-address');
  const { userId } = useParams();
  const stammdaten = participantDataStore.stammdaten;
  const [isSaving, setIsSaving] = useState(false);
  const [showError, setShowError] = useState(false);
  const wasOpened = useRef(false);

  const Form = useForm<UpdateAddressFormValues>({
    validateInputOnChange: true,
    validate: zod4Resolver(updateAddressFormSchema),
    initialValues: {
      [Address]: '',
      [PLZ]: '',
      [Ort]: '',
      movedAt: null,
    },
  });

  useEffect(() => {
    const justOpened = opened && !wasOpened.current;
    wasOpened.current = opened;
    if (!justOpened) return;

    Form.setValues({
      [Address]: stammdaten?.[Address] ?? '',
      [PLZ]: String(stammdaten?.[PLZ] ?? ''),
      [Ort]: stammdaten?.[Ort] ?? '',
      movedAt: null,
    });
    Form.clearErrors();
    setShowError(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, stammdaten]);

  const handleSave = async () => {
    const validation = Form.validate();
    if (!userId || validation.hasErrors) return;

    setIsSaving(true);
    setShowError(false);

    try {
      const { movedAt, ...patch } = updateAddressFormSchema.parse(Form.values);
      await onboardingChecklistStore.updateParticipantAddress(userId, {
        ...patch,
        movedAt: DateTime.fromJSDate(movedAt).toISODate()!,
      });
      participantDataStore.retrieveUserInformation(userId);
      onClose();
    } catch {
      setShowError(true);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <BaseModal
      opened={opened}
      onClose={onClose}
      title={t('Title')}
      withCloseButton
      buttons={[
        <Button
          key="save"
          disabled={!Form.isValid() || isSaving}
          onClick={() => void handleSave()}
        >
          {t('common:Buttons.Save')}
        </Button>,
        <Button key="cancel" variant="subtle" onClick={onClose}>
          {t('common:Buttons.Cancel')}
        </Button>,
      ]}
    >
      <Flex direction="column" gap="10px" w="100%">
        <TextInput
          required
          label={t('Inputs.Address')}
          {...Form.getInputProps(Address)}
        />
        <TextInput
          required
          label={t('Inputs.PLZ')}
          {...Form.getInputProps(PLZ)}
        />
        <TextInput
          required
          label={t('Inputs.Ort')}
          {...Form.getInputProps(Ort)}
        />
        <DatePickerInput
          required
          locale={i18n.language}
          maxDate={DateTime.now().toJSDate()}
          leftSection={<MyIcon name="calendar" style={{ height: '20px' }} />}
          leftSectionPointerEvents="none"
          label={t('Inputs.MovedAt')}
          placeholder={t('Inputs.MovedAtPlaceholder')}
          value={Form.values.movedAt}
          onChange={(date: DateValue) =>
            Form.setFieldValue(
              'movedAt',
              date ? DateTime.fromJSDate(date).toJSDate() : null,
            )
          }
        />
        <Text size="sm" c="dimmed">
          {t('NachversandNote')}
        </Text>
        {showError && (
          <Text size="sm" c="red">
            {t('Error')}
          </Text>
        )}
      </Flex>
    </BaseModal>
  );
});
